// Phase 36 Batch O — Shared bucket-based stage filter dropdown.
//
// Used by both Dashboard and Portfolio Overview to filter aggregate
// KPIs + tables by pipeline-stage BUCKET (Pipeline · Negotiating ·
// Under Contract · Owned · Sold) plus a few convenience aliases
// ("Active" = Pipeline+Negotiating+UC+Owned, "All", "All except passed",
// "Passed (browse only)").
//
// Buckets and labels mirror /app/backend/pipeline_stages.py exactly —
// kept in one place here so the two pages stay in sync. If buckets
// change on the backend, update this list AND TOTALABLE_BUCKETS /
// ACTIVE_BUCKETS in `pipeline_stages.py`.
//
// Renders as a shadcn Select (already in /components/ui/select).

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Single source of truth for the dropdown labels. Bucket keys mirror
// PIPELINE_BUCKETS on the backend.
export const STAGE_FILTER_OPTIONS = [
  { value: "all_active",      label: "Active deals",      group: "preset",
    hint: "Pipeline + Negotiating + Under Contract + Owned" },
  { value: "all_excl_passed", label: "All except passed", group: "preset" },
  { value: "all",             label: "All deals",         group: "preset" },
  // Per-bucket
  { value: "pipeline",        label: "Pipeline",          group: "bucket",
    hint: "Prospect + Watching" },
  { value: "negotiating",     label: "Negotiating",       group: "bucket",
    hint: "Offer Submitted + Negotiating" },
  { value: "under_contract",  label: "Under Contract",    group: "bucket" },
  { value: "owned",           label: "Owned",             group: "bucket" },
  { value: "sold",            label: "Sold",              group: "bucket" },
  { value: "passed",          label: "Passed (browse)",   group: "bucket" },
];

export function stageFilterLabel(value) {
  return STAGE_FILTER_OPTIONS.find((o) => o.value === value)?.label || value;
}

export default function StageFilterDropdown({ value, onChange, testid = "stage-filter-dropdown" }) {
  // Phase 43 Batch BE — Group 5 / Finding D. Default trigger shows
  // BOTH the SelectItem's label AND its hint (because Radix echoes
  // the entire matched SelectItem children into the trigger), which
  // overflows the fixed 200px trigger and truncates as "Active
  // deals..." for the "Pipeline + Negotiating + ..." hint. Resolution:
  // pass `children` to <SelectValue> so the trigger renders ONLY the
  // resolved label, while the SelectItem dropdown still shows
  // label + hint two-line layout.
  const triggerLabel = value ? stageFilterLabel(value) : stageFilterLabel("all_active");
  return (
    <div className="inline-flex items-center gap-2" data-testid={`${testid}-wrap`}>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        Stage
      </span>
      <Select value={value || "all_active"} onValueChange={onChange}>
        <SelectTrigger
          data-testid={testid}
          className="h-9 w-[200px] rounded-full border-slate-200 bg-white text-sm font-medium text-slate-700 hover:border-slate-300"
        >
          <SelectValue>{triggerLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {STAGE_FILTER_OPTIONS.map((o, i) => (
            <SelectItem
              key={o.value}
              value={o.value}
              data-testid={`${testid}-option-${o.value}`}
              className="cursor-pointer"
            >
              <div className="flex flex-col">
                <span className="text-sm">{o.label}</span>
                {o.hint ? (
                  <span className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">
                    {o.hint}
                  </span>
                ) : null}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
